
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';
import ScrollToTop from '../../components/base/ScrollToTop';
import { blogPosts } from '../../data/blogPosts';

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState<string>('Alle');
  const categories = Array.from(new Set(blogPosts.map(post => post.category)));
  
  const filteredPosts = selectedCategory === 'Alle' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  // Scroll to top when component mounts or category changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedCategory]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Blog - Guides og Tips Om Hesteudstyr og Pleje"
        description="Læs ekspertguides om hesteudstyr, sadeltilpasning, hovpleje, fodring og meget mere. Praktiske tips og råd fra erfarne hestefolk."
        url="https://equinord.dk/blog"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "EquiNord Blog",
          "description": "Ekspertguides og tips om hesteudstyr og hestepleje",
          "url": "https://equinord.dk/blog"
        }}
      />
      <Header />
      <main>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              EquiNord Blog
            </h1>
            <p className="text-xl md:text-2xl opacity-95 mb-8 leading-relaxed">
              Ekspert vejledning, tips og råd om hesteudstyr, pleje og rideteknik
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm opacity-90">
              <div className="flex items-center space-x-2">
                <i className="ri-book-open-line text-lg"></i>
                <span>Ekspert guides</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-lightbulb-line text-lg"></i>
                <span>Praktiske tips</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-heart-line text-lg"></i>
                <span>Hestesundhed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            <button 
              onClick={() => setSelectedCategory('Alle')}
              className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 ${
                selectedCategory === 'Alle'
                  ? 'bg-emerald-600 text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border border-gray-200'
              }`}
            >
              Alle
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-emerald-600 text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-gray-100"
              >
                <div className="relative overflow-hidden aspect-[2/1]">
                  <img
                    src={post.image}
                    alt={post.title}
                    width="800"
                    height="400"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://via.placeholder.com/800x400/10b981/ffffff?text=${encodeURIComponent(post.title)}`;
                    }}
                  />
                  <div className="absolute top-4 left-4 bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                    {post.category}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <i className="ri-calendar-line"></i>
                      <span>{new Date(post.date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <span className="mx-3">•</span>
                    <div className="flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors leading-tight">
                    {post.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed text-base">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-emerald-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-600 font-medium">{post.author}</span>
                    </div>
                    <span className="text-emerald-600 font-semibold flex items-center group-hover:translate-x-1 transition-transform duration-200">
                      Læs mere
                      <i className="ri-arrow-right-line ml-2"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ScrollToTop />
      </main>
      <Footer />
    </div>
  );
}

